import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { logError } from 'services/logging/logging';
import { setUiConfig } from 'stores/tasks/tasks';
import { CANCELLED_ERROR } from 'support/helpers/hooks/useDebouncePromise';
import { useDebouncedWatch } from 'support/helpers/hooks/useDebounceWatch';
import { TaskBaseDTO } from 'support/types';

import { getTaskUiConfig } from '../hooks/getTaskUiConfig';

type TaskReactiveUiConfigProps = {
  actions: NonNullable<TaskBaseDTO['actions']> | undefined;
};

const DEBOUNCE_TIME = 200;

export const TaskReactiveUiConfig = ({ actions }: TaskReactiveUiConfigProps) => {
  const { watch, reset } = useFormContext();

  const debouncedFormData = useDebouncedWatch(watch, DEBOUNCE_TIME);

  useEffect(() => {
    const processData = async () => {
      if (!debouncedFormData || Object.keys(debouncedFormData).length === 0) {
        return;
      }

      try {
        const result = await getTaskUiConfig({
          data: debouncedFormData,
          actions,
        });

        if (!result) {
          return;
        }

        const { data, uiConfig } = result;

        reset(data ?? undefined, {
          keepIsSubmitted: true,
          keepErrors: true,
          keepDirty: true,
        });

        setUiConfig(uiConfig);
      } catch (error) {
        if (error instanceof Error && error.message !== CANCELLED_ERROR) {
          logError(error);
        }
      }
    };

    processData();
  }, [actions, debouncedFormData, reset]);

  return null;
};
